import { Breadcrumbs } from 'baseui/breadcrumbs';
import { Tab, Tabs } from 'baseui/tabs';
import qs from 'query-string';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadingPage } from '../../Components/LoadingOverlay';
import { useCurrentProvider } from '../../Components/Permissions';
import { useQueryParams } from '../../Hooks';
import { Text } from '../../globalStyles';
import { Entitlement, Permission, useOrganizationTopLevelQuery } from '../../graphQL';
import { hasEntitlement } from '../../utils';
import { CampusAnnouncementsFromParams } from '../CampusAnnouncements/CampusAnnouncements';
import { HandbookFromParams } from '../Handbook/Handbook';
import { UnexpectedError } from '../Shared';
import { Overrides } from '../Users/baseuiOverrides';
import { OrganizationCareFlows } from './OrganizationCareFlows';
import { OrganizationCareOps } from './OrganizationCareOps';
import { OrganizationCarePeriods } from './OrganizationCarePeriods';
import { OrganizationCrisisTeam } from './OrganizationCrisisTeam';
import { OrganizationEligibility } from './OrganizationEligibility';
import { OrganizationEntitlements } from './OrganizationEntitlements';
import { OrganizationOverview } from './OrganizationOverview';
import { OrganizationPatients } from './OrganizationPatients';
import { OrganizationReporting } from './OrganizationReporting';
import { OrganizationSSOConfig } from './OrganizationSSOConfig';
import { OrganizationStaff } from './OrganizationStaff';
import { OrganizationTopDisplay } from './OrganizationTopDisplay';
import { OrgTabProps, useOrganizationId } from './util';
import { OrganizationSAIOptions } from './OrganizationSAIOptions';

type TabKey =
  | 'reporting'
  | 'handbook'
  | 'overview'
  | 'careOps'
  | 'students'
  | 'patients'
  | 'staff'
  | 'entitlements'
  | 'resources'
  | 'carePeriods'
  | 'careTypes'
  | 'sso'
  | 'saiOptions';

type OrgListItem = {
  content: React.FC<OrgTabProps>;
  title: string;
  key: TabKey;
  hidden?: boolean;
};

export function OrganizationAdmin() {
  const id = useOrganizationId();
  const [queryParams, updateQueryParams] = useQueryParams();
  const { search } = useLocation();
  const { hasPermission, featureFlags } = useCurrentProvider();
  const { data, loading, error, refetch } = useOrganizationTopLevelQuery({ variables: { id } });

  const refetchHeader = async (): Promise<void> => {
    // Downstream callers do not want the data, they just want to trigger the refetch
    await refetch();
  };

  const usesCrisisTeam = data?.organization?.entitlements
    ? hasEntitlement(data.organization, Entitlement.CrisisTeam)
    : false;

  const usesDGM = data?.organization?.entitlements
    ? hasEntitlement(data.organization, Entitlement.DedicatedGroupModel)
    : false;

  const usesSessionsModel = data?.organization?.entitlements
    ? hasEntitlement(data.organization, Entitlement.SessionsModel)
    : false;

  const canAccessSaiOptions = data?.organization?.entitlements
    ? hasEntitlement(data.organization, Entitlement.Sai) && hasPermission(Permission.MantraAdmin)
    : false;

  const tabs = useMemo(
    () =>
      [
        {
          title: 'Reporting',
          key: 'reporting',
          content: OrganizationReporting,
          hidden: !hasPermission(Permission.Reporting),
        },
        { title: 'Overview', key: 'overview', content: OrganizationOverview },
        {
          title: 'Handbook',
          key: 'handbook',
          content: HandbookFromParams,
        },
        {
          title: 'Updates',
          key: 'updates',
          content: CampusAnnouncementsFromParams,
        },
        {
          title: 'Care Ops',
          key: 'careOps',
          content: OrganizationCareOps,
          hidden: !(usesDGM || usesSessionsModel) || !hasPermission(Permission.UtilizationView),
        },
        { title: 'Staff', key: 'staff', content: OrganizationStaff },
        { title: 'Patients', key: 'patients', content: OrganizationPatients },
        { title: 'Care Types', key: 'careTypes', content: OrganizationCareFlows },
        { title: 'Entitlements', key: 'entitlements', content: OrganizationEntitlements },
        { title: 'Care Periods', key: 'carePeriods', content: OrganizationCarePeriods },
        { title: 'Eligibility', key: 'eligibility', content: OrganizationEligibility },
        {
          title: 'Single Sign-On',
          key: 'sso',
          content: OrganizationSSOConfig,
        },
        {
          title: 'Crisis Team',
          key: 'crisisTeam',
          content: OrganizationCrisisTeam,
          hidden: !(hasPermission(Permission.CrisisTeamEdit) && usesCrisisTeam),
        },
        {
          title: 'SAI Options',
          key: 'saiOptions',
          content: OrganizationSAIOptions,
          hidden: !canAccessSaiOptions,
        },
      ].filter(t => !t.hidden) as OrgListItem[],
    [usesCrisisTeam, featureFlags, hasPermission, usesDGM, usesSessionsModel, canAccessSaiOptions]
  );

  const activeTab = (() => {
    const { tab } = qs.parse(search);
    const tabItem = tabs.find(t => t.key === tab) ?? tabs[0];
    return tabItem.key;
  })();

  const setActiveTab = (activeKey: string) => {
    updateQueryParams({ ...queryParams, tab: activeKey });
  };

  if (loading) return <LoadingPage />;
  if (error || !data) return <UnexpectedError />;

  const { organization } = data;

  return (
    <div className="bg-white">
      <div className="ph5 pv3">
        <Breadcrumbs>
          <Text.breadcrumbLink to="/organizations">Organizations</Text.breadcrumbLink>
          <Text.body kind="grayText">{organization.name}</Text.body>
        </Breadcrumbs>
      </div>
      <OrganizationTopDisplay organization={organization} />
      <Tabs
        activeKey={activeTab}
        onChange={({ activeKey }) => setActiveTab(activeKey as string)}
        overrides={Overrides.TabsWrap}
      >
        {tabs.map(({ content: C, title, key }) => (
          <Tab title={title} key={key} overrides={Overrides.Tab}>
            <C setActiveTab={setActiveTab} refetchHeader={refetchHeader} />
          </Tab>
        ))}
      </Tabs>
    </div>
  );
}
